/**
 * Phase 27.48 — Manage Participants dialog.
 *
 * Lists all current participating agents with a remove button, and
 * provides a searchable picker to add more (from agentAPI.list). KR
 * and Agency Admins only — the parent gates the button on those roles.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Search, Plus, X, UserPlus } from 'lucide-react';
import { agentAPI, eventRequestsAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function ManageParticipantsDialog({
  open, onOpenChange, requestId, current = [], onSaved,
}) {
  const [participants, setParticipants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setParticipants((current || []).map((p) => ({
      user_id: p.user_id || null,
      npn: p.npn || null,
      name: p.name || '',
      email: p.email || null,
    })));
    setQ('');
    // Preload a batch of agents so the picker is instant.
    setLoading(true);
    agentAPI.list({ limit: 500 })
      .then((r) => setAgents(r.data?.items || r.data || []))
      .catch(() => setAgents([]))
      .finally(() => setLoading(false));
  }, [open, current]);

  const selectedKeys = useMemo(
    () => new Set(participants.map((p) => `${p.npn || ''}::${p.user_id || ''}`)),
    [participants],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return agents.slice(0, 25);
    return agents
      .filter((a) => {
        const name = `${a.first_name || ''} ${a.last_name || ''} ${a.name || ''}`.toLowerCase();
        return name.includes(term) || (a.npn || '').includes(term) || (a.email || '').toLowerCase().includes(term);
      })
      .slice(0, 25);
  }, [agents, q]);

  const addAgent = useCallback((agent) => {
    const entry = {
      user_id: agent.user_id || null,
      npn: agent.npn || null,
      name: agent.name
        || `${agent.first_name || ''} ${agent.last_name || ''}`.trim()
        || agent.email
        || `NPN ${agent.npn}`,
      email: agent.email || null,
    };
    const key = `${entry.npn || ''}::${entry.user_id || ''}`;
    if (selectedKeys.has(key)) {
      toast.info('Already assigned');
      return;
    }
    setParticipants((prev) => [...prev, entry]);
  }, [selectedKeys]);

  const removeAgent = (idx) => {
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      await eventRequestsAPI.updateParticipants(requestId, participants);
      toast.success('Participants updated');
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save participants');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="manage-participants-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus size={18} /> Manage Participants
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current list */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Assigned ({participants.length})
            </div>
            {participants.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No participants yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {participants.map((p, i) => (
                  <Badge
                    key={`${p.npn || ''}::${p.user_id || ''}::${i}`}
                    variant="outline"
                    className="bg-slate-50 border-slate-200 text-slate-800 text-xs pl-2 pr-1 py-0.5 flex items-center gap-1"
                    data-testid={`participant-chip-${i}`}
                  >
                    {p.name}{p.npn ? ` · ${p.npn}` : ''}
                    <button
                      type="button"
                      onClick={() => removeAgent(i)}
                      className="ml-1 hover:bg-rose-100 rounded p-0.5"
                      title="Remove participant"
                      data-testid={`participant-remove-${i}`}
                    >
                      <X size={12} className="text-rose-600" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Picker */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Add agent
            </div>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, NPN or email…"
                className="pl-9"
                data-testid="participant-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {loading ? (
                <div className="py-6 flex justify-center">
                  <Loader2 className="animate-spin text-slate-400" size={16} />
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">No agents found.</p>
              ) : (
                filtered.map((a) => {
                  const key = `${a.npn || ''}::${a.user_id || ''}`;
                  const already = selectedKeys.has(key);
                  const display = a.name
                    || `${a.first_name || ''} ${a.last_name || ''}`.trim()
                    || a.email || `NPN ${a.npn}`;
                  return (
                    <button
                      type="button"
                      key={key || a.npn || Math.random()}
                      disabled={already}
                      onClick={() => addAgent(a)}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-slate-50 ${already ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`participant-add-${a.npn || 'noNpn'}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-slate-900 truncate">{display}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {a.npn ? `NPN ${a.npn}` : ''}{a.email ? ` · ${a.email}` : ''}
                        </div>
                      </div>
                      <Plus size={14} className="text-slate-400" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button" variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="participants-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
